/**
 * ProviderSwitcher - Recreation of Base44 ProviderSwitcher Component
 * Multi-cloud provider selection with resource counts and theming
 * 
 * @description Professional provider switching interface with AWS, GCP, Azure
 * support, real-time resource counts, and provider-specific styling
 * Phase 3: Updated for CloudProvider type compatibility
 */

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Cloud } from 'lucide-react';
import type { CloudProvider } from '@/types/cloudProvider';

// Provider configuration matching Base44 patterns
const providers = [
  { 
    id: 'aws' as CloudProvider, 
    name: 'AWS', 
    color: 'bg-orange-500', 
    textColor: 'text-orange-700', 
    bgLight: 'bg-orange-50' 
  },
  { 
    id: 'gcp' as CloudProvider, 
    name: 'Google Cloud', 
    color: 'bg-blue-500', 
    textColor: 'text-blue-700', 
    bgLight: 'bg-blue-50' 
  },
  { 
    id: 'azure' as CloudProvider, 
    name: 'Azure', 
    color: 'bg-cyan-500', 
    textColor: 'text-cyan-700', 
    bgLight: 'bg-cyan-50' 
  }
];

// TypeScript interfaces for type safety - Phase 3: Updated for CloudProvider
interface ProviderSwitcherProps {
  activeProvider: CloudProvider;
  onProviderChange: (provider: CloudProvider) => void;
  resourceCounts: Record<CloudProvider, number>;
}

export function ProviderSwitcher({ 
  activeProvider, 
  onProviderChange, 
  resourceCounts 
}: ProviderSwitcherProps) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-8">
      {/* Provider label with icon */}
      <div className="flex items-center gap-2">
        <Cloud className="w-5 h-5 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">Cloud Provider:</span>
      </div>
      
      {/* Provider buttons */}
      <div className="flex gap-2 flex-wrap">
        {providers.map(provider => (
          <Button
            key={provider.id}
            variant={activeProvider === provider.id ? "default" : "outline"}
            onClick={() => onProviderChange(provider.id)}
            className={`relative ${
              activeProvider === provider.id 
                ? `${provider.color} hover:opacity-90 text-white` 
                : `hover:${provider.bgLight} hover:${provider.textColor} border-border`
            }`}
          >
            <div className="flex items-center gap-2">
              {/* Provider indicator dot */}
              <div className={`w-3 h-3 rounded-full ${provider.color}`} />
              
              {/* Provider name */}
              <span>{provider.name}</span>
              
              {/* Resource count badge */}
              <Badge variant="secondary" className="ml-1 text-xs">
                {resourceCounts[provider.id] || 0}
              </Badge>
            </div>
          </Button>
        ))}
      </div>
    </div>
  );
}

export default ProviderSwitcher;